import { useState, useMemo } from 'react';
import { useFormikContext } from 'formik';
import { Spinner } from '@fluentui/react';
import AffinityCourseTable from './AffinityCourseTable';
import { useBoolean } from "@fluentui/react-hooks";
import ErrorComponent from '../../../../Util/ErrorComponent';
import MessageBarComponent from '../../../../Util/MessageBarComponent';
import FluentSelect from '../../../../Forms/FluentSelect';

const MAX_SELECTABLE = 8;

const AffinityCourses = ({ fieldName = 'courses', ...props }) => {
  const { setFieldValue, values } = useFormikContext();
  const selectedCourses = values[fieldName] || [];
  const [selectedCourse, setSelectedCourse] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [showAddCourse, setShowAddCourse] = useState(false);
  const [showDeleteModal, { toggle: toggleDeleteModal }] = useBoolean(false);

  // dataOptions.courses is the array of all available courses
  const allCourses = values.dataOptions?.courses || [];
  const noEligibleData = !allCourses || allCourses.length === 0;
  const loading = useMemo(() => noEligibleData, [noEligibleData]);
  const resourceOptions = allCourses;

  const handleRemoveCourse = (item) => {
    if (!item || !item.crsid) return;
    const updated = selectedCourses.filter(c => c.crsid !== item.crsid);
    // Reassign order to be contiguous
    const reOrdered = updated.map((course, idx) => ({ ...course, order: idx + 1 }));
    setFieldValue(fieldName, reOrdered);
    setShowSuccess(true);
  };

  const mapNewContent = (id) => {
    if (!id) return;
    const item = resourceOptions.find(x => x.crsid === id.value);
    if (selectedCourses.some(c => c.crsid === item.crsid)) return; // Prevent duplicates
    item.order = selectedCourses.length + 1; // Assign order based on current length
    const newlist = [...selectedCourses, item];
    if (newlist.length > MAX_SELECTABLE) {
        setShowError(true);
        setErrorMsg(`You can only select up to ${MAX_SELECTABLE} courses.`);
        return;
    }
    setFieldValue(fieldName, newlist);
    setShowAddCourse(false);
    setShowSuccess(true);
  };

  if (loading) return <Spinner className="absolute-center" label="Loading Courses..." />;

  return (
    <>
      <MessageBarComponent
        message="Courses updated. Remember to save."
        showMessage={showSuccess}
        setShowMessage={setShowSuccess}
      />
      <ErrorComponent
        errorMessage={errorMsg}
        showError={showError}
        setShowError={setShowError}
      />
      {showAddCourse && (
        <div className="max-800">
          <FluentSelect
            options={allCourses.map(c => ({ value: c.crsid, label: `${c.crsid} - ${c.title}` }))}
            onChange={mapNewContent}
            label='Eligible Courses'
            component={FluentSelect}
          />
          <label className="preview-upload-help">Search for courses by Title or ID. Then select to add.</label>
        </div>
      )}
      <AffinityCourseTable
        content={selectedCourses}
        selectedContent={selectedCourse}
        setSelectedContent={setSelectedCourse}
        deleteOnClick={handleRemoveCourse}
        maxSelectable={MAX_SELECTABLE}
        showAddContent={showAddCourse}
        loading={loading}
        createOnClick={() => setShowAddCourse(true)}
        {...props}
      />
      {/* Add DeleteContentModal if needed */}
    </>
  );
};

export default AffinityCourses;
